import React, { useState } from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { PageHero } from "../components/PageHero";
import { Calendar, CheckCircle2, AlertCircle, Loader2, ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";
import { submitLead, emptyLeadForm } from "../lib/api";

const HIGHLIGHTS = [
  "Walkthrough of LXP, course catalogs and admin analytics",
  "Custom learning path tailored to your team’s tech stack",
  "Live Q&A with a SkillsGain solutioning architect",
  "Pricing & rollout plan for cohorts of 25 to 5,000+ learners",
];

const INTEREST_OPTIONS = [
  "Generative AI & LLM",
  "Cloud (AWS / Azure / GCP)",
  "Cybersecurity",
  "Data & Analytics",
  "Software Engineering",
  "Learning Experience Platform (LXP)",
  "Other",
];

const DemoPage = () => {
  const [form, setForm] = useState(emptyLeadForm());
  const [status, setStatus] = useState({ state: "idle", message: "" });

  const submit = async (e) => {
    e.preventDefault();
    setStatus({ state: "loading", message: "" });
    try {
      await submitLead({ ...form, source: "demo-page", formType: "demo" });
      setStatus({ state: "success", message: "We’ve received your request. A learning advisor will reach out within 24 hours." });
      setForm(emptyLeadForm());
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setStatus({
        state: "error",
        message: typeof detail === "string" ? detail : "Couldn’t submit your request. Please try again.",
      });
    }
  };

  return (
    <div className="bg-white min-h-screen">
      <Header />
      <PageHero
        eyebrow="JOIN A DEMO SESSION"
        title="See SkillsGain in action — book a live walkthrough"
        subtitle="A 30-minute personalized demo for L&D leaders. No slides, just the real product and your real use cases."
      />

      <section className="max-w-7xl mx-auto px-4 py-16 grid lg:grid-cols-5 gap-10">
        <div className="lg:col-span-2 space-y-6">
          <div className="inline-flex items-center gap-2 bg-[#6BBE32]/10 border border-[#6BBE32]/30 px-3 py-1.5 rounded-full text-xs font-semibold text-[#4F9622]">
            <Calendar size={14}/> 30-min Live Walkthrough
          </div>
          <h2 className="text-[#15348C]" style={{fontSize: "1.75rem"}}>What you’ll see in the demo</h2>
          <ul className="space-y-3">
            {HIGHLIGHTS.map((h) => (
              <li key={h} className="flex gap-3 text-gray-700">
                <CheckCircle2 size={20} className="text-[#6BBE32] shrink-0 mt-0.5" />
                <span>{h}</span>
              </li>
            ))}
          </ul>
          <div className="bg-[#15348C] text-white rounded-2xl p-6 mt-6">
            <p className="text-xs font-bold tracking-widest text-[#8BD835]">TRUSTED BY</p>
            <p className="mt-2 text-2xl font-semibold">250+ enterprises</p>
            <p className="text-sm text-white/80">across BFSI, IT services, manufacturing, healthcare and the public sector.</p>
          </div>
        </div>

        <div className="lg:col-span-3 bg-[#F8F9FA] border border-gray-100 rounded-2xl p-8">
          <h2 className="text-[#15348C]" style={{fontSize: "1.5rem"}}>Request your demo</h2>
          <p className="text-sm text-gray-500 mt-1">Fill in the form and we’ll confirm a slot in your timezone.</p>

          <form onSubmit={submit} className="mt-6 grid sm:grid-cols-2 gap-4" data-testid="demo-form">
            <Field label="Full Name" name="name" testid="demo-name" required value={form.name} onChange={(v) => setForm({...form, name: v})}/>
            <Field label="Work Email" name="email" testid="demo-email" type="email" required value={form.email} onChange={(v) => setForm({...form, email: v})}/>
            <Field label="Phone" name="mobile_no" testid="demo-phone" value={form.mobile_no} onChange={(v) => setForm({...form, mobile_no: v})}/>
            <Field label="Company" name="company_name" testid="demo-company" value={form.company_name} onChange={(v) => setForm({...form, company_name: v})}/>

            <div className="sm:col-span-2">
              <label className="text-sm font-semibold text-[#15348C]">Area of Interest</label>
              <select name="interest" value={form.interest} onChange={(e) => setForm({...form, interest: e.target.value})}
                data-testid="demo-interest"
                className="mt-1 w-full bg-white border border-gray-200 rounded-lg px-3 py-2 outline-none focus:border-[#6BBE32] transition-colors">
                <option value="">Select a track…</option>
                {INTEREST_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>

            <div className="sm:col-span-2">
              <label className="text-sm font-semibold text-[#15348C]">Tell us about your team</label>
              <textarea name="notes" value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})}
                data-testid="demo-message"
                rows={4} placeholder="Team size, current learning stack, top skill gaps…"
                className="mt-1 w-full bg-white border border-gray-200 rounded-lg px-3 py-2 outline-none focus:border-[#6BBE32] transition-colors"/>
            </div>

            <div className="sm:col-span-2 flex flex-wrap items-center gap-4">
              <Button type="submit" disabled={status.state === "loading"} data-testid="demo-submit"
                className="bg-[#F57C20] hover:bg-[#D86810] text-white font-semibold px-6 h-12 rounded-md shadow-lg shadow-[#F57C20]/30 disabled:opacity-60">
                {status.state === "loading"
                  ? (<><Loader2 size={16} className="mr-2 animate-spin"/> Submitting…</>)
                  : (<>Book My Demo <ArrowRight size={17} className="ml-1"/></>)}
              </Button>
              {status.state === "success" && (
                <span data-testid="demo-success" className="text-[#4F9622] text-sm flex items-center gap-1">
                  <CheckCircle2 size={16}/> {status.message}
                </span>
              )}
              {status.state === "error" && (
                <span data-testid="demo-error" className="text-red-600 text-sm flex items-center gap-1">
                  <AlertCircle size={16}/> {status.message}
                </span>
              )}
            </div>
          </form>
        </div>
      </section>

      <Footer />
    </div>
  );
};

const Field = ({ label, name, value, onChange, type = "text", required, testid }) => (
  <div>
    <label className="text-sm font-semibold text-[#15348C]">{label}{required && <span className="text-[#F57C20]">*</span>}</label>
    <input type={type} name={name} value={value} required={required} onChange={(e) => onChange(e.target.value)}
      data-testid={testid}
      className="mt-1 w-full bg-white border border-gray-200 rounded-lg px-3 py-2 outline-none focus:border-[#6BBE32] transition-colors"/>
  </div>
);

export default DemoPage;
